import { useEffect, useState } from 'react'

/**
 * Custom hook to detect scrolling on the page content element
 * @returns {boolean} isScrolled - Whether the page has been scrolled beyond the threshold
 */
const useHeaderScroll = () => {
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    // Update body class based on scroll state
    if (isScrolled) {
      document.body.classList?.add?.('wp-header-shrunk')
      document.body.classList?.remove?.('wp-header-fullsize')
    } else {
      document.body.classList?.add?.('wp-header-fullsize')
      document.body.classList?.remove?.('wp-header-shrunk')
    }

    const handleScroll = (event) => {
      const scrollPosition = event.target?.scrollTop
      if (scrollPosition > 10 && !isScrolled) {
        setIsScrolled(true)
      } else if (scrollPosition <= 10 && isScrolled) {
        setIsScrolled(false)
      }
    }

    let pageContentElement = null;
    let observer = null;

    // Function to attach the scroll event listener
    const attachScrollListener = () => {
      const element = document?.querySelector?.('.pageContent');
      pageContentElement = element;
      if (pageContentElement) {
        // pageContentElement found, attaching scroll listener
        pageContentElement?.addEventListener?.('scroll', handleScroll);
        
        // If we found the element and observer exists, disconnect it
        if (observer) {
          observer?.disconnect?.();
          observer = null;
        }
      }
    };

    // Try to find the element immediately
    attachScrollListener();

    // If not found, set up a MutationObserver to watch for it
    if (!pageContentElement) {
      // pageContentElement not found, setting up observer
      const newObserver = new MutationObserver((mutations) => {
        if (!pageContentElement) {
          attachScrollListener();
        }
      });
      
      observer = newObserver;
      
      // Start observing the document body for DOM changes
      if (observer && document && document.body) {
        observer.observe(document.body, {
        childList: true,
        subtree: true
        });
      }
    }

    // Cleanup function
    return () => {
      if (pageContentElement) {
        pageContentElement?.removeEventListener?.('scroll', handleScroll);
      }
      if (observer) {
        observer?.disconnect?.();
      }
      // Clean up body classes on unmount
      document.body.classList?.remove?.('header-fullsize', 'header-shrunk');
    };
  }, [isScrolled]);

  return isScrolled;
}

export default useHeaderScroll